import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import Markdown from "marked-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  PerformanceContainer,
  AnalysisContainer,
  MarkdownContent,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  AccordionTitle,
} from "../../performance/PerformanceStyles";
import { AnalysisSkeleton } from "../../performance/AnalysisSkeleton";
import {
  selectPerformanceReportStoreId,
  selectKPIAnalysis,
  selectKPIAnalysisLoading,
  selectIsReportAPIAvailable,
} from "store/performance-report/PerformanceReportSelectors";
import { parseMarkdownSections } from "utils/MarkdownParser";

interface AnalysisComponentProps {}

const AnalysisComponent: React.FC<AnalysisComponentProps> = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "section-1",
  ]);
  const performanceStoreId = useSelector(selectPerformanceReportStoreId);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  const analysisData = useSelector(selectKPIAnalysis);
  const isLoading = useSelector(selectKPIAnalysisLoading);

  const sections = useMemo(() => {
    if (!analysisData?.narrative) return [];
    return parseMarkdownSections(analysisData.narrative);
  }, [analysisData?.narrative]);

  const handleAccordionChange =
    (sectionId: string) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSections((prev) =>
        isExpanded
          ? [...prev, sectionId]
          : prev.filter((id) => id !== sectionId),
      );
    };

  const renderLoadingSkeleton = () => <AnalysisSkeleton />;

  const renderAnalysisContent = () => {
    if (!analysisData || sections.length === 0) return null;

    return (
      <AnalysisContainer>
        {sections.map((section, index) => (
          <StyledAccordion
            key={section.id}
            expanded={expandedSections.includes(section.id)}
            onChange={handleAccordionChange(section.id)}
          >
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${section.id}-content`}
              id={`${section.id}-header`}
            >
              <AccordionTitle>{section.title}</AccordionTitle>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <MarkdownContent>
                <Markdown gfm={false} breaks={true}>
                  {section.content}
                </Markdown>
              </MarkdownContent>
            </StyledAccordionDetails>
          </StyledAccordion>
        ))}
      </AnalysisContainer>
    );
  };

  if (!isReportAPIAvailable || (!analysisData && !isLoading)) {
    return (
      <PerformanceContainer>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#4D4D4E",
          }}
        >
          <Typography variant="h4" style={{ fontWeight: "bold" }}>
            —
          </Typography>
        </div>
      </PerformanceContainer>
    );
  }

  return (
    <>
      {performanceStoreId && (
        <PerformanceContainer>
          {(isLoading || !analysisData) && renderLoadingSkeleton()}
          {!isLoading && analysisData && renderAnalysisContent()}
        </PerformanceContainer>
      )}
    </>
  );
};

export default AnalysisComponent;
